import { useState, type Dispatch, type SetStateAction } from "react";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ACTION_CATEGORIES,
  type ActionCategory,
  type ConnectionDetail,
} from "@typedoc/contracts/connections";
import { Button, Loader, Segmented, Skeleton, Switch } from "@typedoc/ui";
import { ErrorNotice } from "../error-notice";
import { normalizeAppError } from "../../lib/app-error";
import { updatePermissions } from "../../lib/connections/api";
import {
  EMPTY_DRAFT,
  isDirty,
  setCategory,
  setLookOnly,
  setOverride,
  toSaveInput,
  type PermissionsDraft,
} from "../../lib/connections/permissions-draft";
import type { ConnectionsContext } from "../../lib/connections/use-connections-scope";
import { connectionsKey, permissionsQueryOptions } from "../../lib/queries/connections";
import { UseInChatRow } from "./use-in-chat-row";
import { cx, DECISION_LABEL, DECISION_OPTIONS, Eyebrow, reasonLabel } from "./parts";

/**
 * Figma 7.17a–i. What a teammate may do in this account, whichever process brings them to it.
 * Who reaches it is not chosen here: a teammate works in an account through a process that uses
 * it, so that relationship is read-only and shows under Used by. Look only and the decisions are
 * a draft until Save, because they are read together: a category change moves every action
 * under it that has no exception of its own. The draft lives with the details page, so it
 * outlasts a visit to another tab, and keeps the revision it began on.
 *
 * Use in chat, above them, is the one control that does not wait for Save: it is a switch for
 * the whole account, not one of the decisions read together. Because it moves the account's
 * revision on, the details page keeps this draft's base in step with it (`rebaseDraft`).
 *
 * A category with no action in this app is greyed out: its decision is kept, for when a later
 * version of the app adds one, but there is nothing in it to decide today.
 */
export function PermissionsTab({
  connection,
  context,
  draft,
  onDraftChange,
  availability,
}: {
  connection: ConnectionDetail;
  context: ConnectionsContext;
  draft: PermissionsDraft;
  onDraftChange: Dispatch<SetStateAction<PermissionsDraft>>;
  /** Use in chat saves by itself, so the details page owns it; this only shows it. */
  availability: { checked: boolean; saving: boolean; onChange: (value: boolean) => void };
}) {
  const queryClient = useQueryClient();
  const permissions = useQuery(permissionsQueryOptions(context, connection.id));
  const [expanded, setExpanded] = useState<ActionCategory | null>(null);
  const editable = connection.can.manage;

  const discard = () => onDraftChange(EMPTY_DRAFT);
  const refresh = () => queryClient.invalidateQueries({ queryKey: connectionsKey(context.scope) });
  const save = useMutation({
    mutationKey: ["save-permissions", connection.id],
    mutationFn: () => {
      const data = permissions.data;
      if (!data) throw new Error("Permissions are not loaded.");
      return updatePermissions(context, connection.id, toSaveInput(draft, data));
    },
    onSuccess: async (next) => {
      queryClient.setQueryData(permissionsQueryOptions(context, connection.id).queryKey, next);
      discard();
      await refresh();
    },
  });
  // Success clears the draft, so an edit made while the save is in flight would be cleared
  // without ever being sent. The controls hold still until the answer is in. They hold still
  // while Use in chat saves too, until the account has been read again: an edit begun before then
  // would record the revision from before the flip, and Save would be refused for it.
  const switching = availability.saving;
  // Read from the mutation cache, not from `save`: this tab unmounts when another is chosen, and
  // a save sent before then is still in flight when it comes back.
  const saving = useIsMutating({ mutationKey: ["save-permissions", connection.id] }) > 0;
  const locked = !editable || saving || switching;
  const failure = save.error;
  const problem = failure ? normalizeAppError(failure) : null;

  if (!permissions.data) {
    return permissions.error ? (
      <ErrorNotice error={normalizeAppError(permissions.error)} />
    ) : (
      <PermissionsSkeleton />
    );
  }

  const data = permissions.data;
  const effectiveLookOnly = draft.lookOnly ?? data.lookOnly;
  const dirty = isDirty(draft);
  const manager =
    connection.scope === "workspace"
      ? "Owners, admins and the person who connected it"
      : "Only its owner";

  return (
    <div className="space-y-5 pb-4">
      <section
        aria-labelledby="decisions-title"
        className="overflow-hidden rounded-xl border border-border bg-card"
      >
        <h2 id="decisions-title" className="sr-only">
          What teammates may do
        </h2>
        <UseInChatRow
          scope={connection.scope}
          checked={availability.checked}
          canManage={editable}
          saving={availability.saving}
          otherSaving={saving}
          onChange={availability.onChange}
        />
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <p id="look-only-title" className="text-[13px]/5 font-medium">
              Look only
            </p>
            <p className="text-xs/[18px] text-muted-foreground">
              Blocks every action except looking and downloading, whatever is set below.
            </p>
          </div>
          <Switch
            checked={effectiveLookOnly}
            disabled={locked}
            onCheckedChange={(value) => onDraftChange(setLookOnly(draft, data, value))}
            aria-labelledby="look-only-title"
            className="mt-0.5"
          />
        </div>
        <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 px-5 pt-4 pb-1">
          <Eyebrow>What teammates may do</Eyebrow>
          <Eyebrow>Applies to every playbook that uses it</Eyebrow>
        </div>
        <ul className="divide-y divide-border">
          {data.categories.map((category) => {
            const meta = ACTION_CATEGORIES[category.key];
            const decision = draft.categories[category.key] ?? category.decision;
            const capped = effectiveLookOnly && category.key !== "look";
            const empty = category.actions.length === 0;
            const open = expanded === category.key;
            const exceptions = category.actions.filter((action) => {
              const override =
                action.tool in draft.overrides ? draft.overrides[action.tool] : action.override;
              return override !== null && override !== undefined && override !== decision;
            }).length;
            return (
              <li key={category.key} className="px-5 py-3" data-empty={empty || undefined}>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <button
                    type="button"
                    className="group flex min-w-[min(100%,16rem)] flex-1 items-start gap-2 text-left disabled:cursor-default disabled:opacity-100"
                    aria-expanded={empty ? undefined : open}
                    aria-labelledby={`category-${category.key}`}
                    aria-describedby={`category-${category.key}-about`}
                    onClick={() => setExpanded(open ? null : category.key)}
                    disabled={empty}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      aria-hidden="true"
                      className={cx(
                        "mt-1 shrink-0 text-subtle-foreground transition-transform duration-fast ease-out motion-reduce:transition-none",
                        open && "rotate-90",
                        empty && "invisible",
                      )}
                    >
                      <path
                        d="M4.5 2.5L8 6l-3.5 3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className={cx("min-w-0", empty && "opacity-50")}>
                      <span
                        id={`category-${category.key}`}
                        className="block text-[13px]/5 font-medium"
                      >
                        {meta.label}
                      </span>
                      <span
                        id={`category-${category.key}-about`}
                        className="block text-xs/[18px] text-muted-foreground"
                      >
                        {meta.description}
                      </span>
                      <span className="mt-0.5 block text-xs/[18px] text-subtle-foreground">
                        {empty
                          ? `${connection.app.name} has no actions here`
                          : `${category.actions.length} action${category.actions.length === 1 ? "" : "s"}`}
                        {exceptions ? ` · ${exceptions} set differently` : ""}
                        {capped && !empty ? " · held at Never by Look only" : ""}
                      </span>
                    </span>
                  </button>
                  <Segmented
                    label={`${meta.label}: decision`}
                    value={capped ? "never" : decision}
                    options={DECISION_OPTIONS}
                    disabled={locked || capped || empty}
                    onValueChange={(value) =>
                      onDraftChange(setCategory(draft, data, category.key, value))
                    }
                  />
                </div>
                {open && (
                  <ul className="enter-rise mt-3 ml-1.5 space-y-2.5 border-l border-border pl-5">
                    {category.actions.map((action) => {
                      const stored = action.override;
                      const override =
                        action.tool in draft.overrides
                          ? (draft.overrides[action.tool] ?? null)
                          : stored;
                      const value = capped ? "never" : (override ?? decision);
                      return (
                        <li
                          key={action.tool}
                          className="flex flex-wrap items-center gap-x-4 gap-y-1.5"
                        >
                          <span className="min-w-[min(100%,14rem)] flex-1">
                            <span className="block text-[13px]/5">
                              {action.name}
                              {override !== null && override !== decision && !capped && (
                                <span className="ml-2 text-xs text-subtle-foreground">
                                  set here, category says {DECISION_LABEL[decision]}
                                </span>
                              )}
                            </span>
                            <span className="block text-xs/[18px] text-muted-foreground">
                              <span className="font-mono text-[11px] break-all">{action.tool}</span>{" "}
                              · {reasonLabel(action.reason)}
                            </span>
                          </span>
                          <Segmented
                            label={`${action.name}: decision`}
                            value={value}
                            options={DECISION_OPTIONS}
                            disabled={locked || capped}
                            onValueChange={(next) =>
                              onDraftChange(
                                setOverride(draft, data, {
                                  tool: action.tool,
                                  stored,
                                  value: next,
                                  inherited: decision,
                                }),
                              )
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
        {!editable && (
          <p className="border-t border-border px-5 py-3 text-xs/[18px] text-muted-foreground">
            {manager} can change what teammates may do.
          </p>
        )}
      </section>

      {problem && (
        <div className="flex flex-col items-start gap-3">
          <ErrorNotice error={problem} className="w-full" />
          {problem.code === "CONFLICT" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                save.reset();
                discard();
                void refresh();
              }}
            >
              Reload
            </Button>
          )}
        </div>
      )}
      {dirty && editable && (
        <section
          aria-label="Unsaved changes"
          className="enter-rise sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-input bg-card px-5 py-3 shadow-[0_8px_24px_#17171a14,0_1px_3px_#17171a0f]"
        >
          <span className="text-[13px]/5 text-body-foreground">
            Unsaved changes. Teammates follow the saved settings until you save.
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={discard} disabled={saving || switching}>
              Discard
            </Button>
            <Button size="sm" onClick={() => save.mutate()} disabled={saving || switching}>
              {saving ? <Loader /> : null}
              Save
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

/** The permissions card's shape: Look only, then a row per category with its decision. */
export function PermissionsSkeleton({ className }: { className?: string | undefined }) {
  return (
    <output
      aria-label="Loading permissions"
      className={cx("block overflow-hidden rounded-xl border border-border bg-card", className)}
    >
      <span className="sr-only">Loading permissions…</span>
      <span className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-3 w-[min(22rem,80%)]" />
        </span>
        <Skeleton className="h-5 w-9 rounded-full" />
      </span>
      {[44, 36, 40, 30].map((width) => (
        <span
          key={width}
          className="flex items-center gap-4 border-b border-border px-5 py-3.5 last:border-b-0"
        >
          <span className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5" style={{ width: `${width}%` }} />
            <Skeleton className="h-3" style={{ width: `${width + 20}%` }} />
          </span>
          <Skeleton className="h-7 w-[168px] shrink-0 rounded-lg max-sm:hidden" />
        </span>
      ))}
    </output>
  );
}
